/**
 * useMemberDetail Hook - Aggregator Pattern
 *
 * Fetches individual member detail including analytics for the modal view.
 * Wraps the generated useGetMember hook.
 *
 * Pattern: AGGREGATOR
 * - Fetches single member detail by merchantId + userId
 * - Only enabled when userId is provided (conditional fetching)
 * - Returns member data, loading, error, and refetch for the modal
 */

import { useGetMember } from '@/generated/hooks';
import type { GetMemberResponseType } from '@zlinkx/shared-v2';

// The inner member data type (unwrapped from DataResponse)
// This matches what the MemberAnalyticsModal expects
type MemberDetailData = GetMemberResponseType['data'];

interface UseMemberDetailOptions {
	/** Merchant ID */
	merchantId: number;
	/** User ID (null when no member selected) */
	userId: string | null;
	/** Enable/disable fetching (default: true) */
	enabled?: boolean;
}

export function useMemberDetail(options: UseMemberDetailOptions) {
	const { merchantId, userId, enabled = true } = options;

	const query = useGetMember(
		{ merchantId, userId: userId ?? '' },
		{ enabled: enabled && !!userId },
	);

	// Generated hooks auto-unwrap DataResponse via createQueryHook,
	// so query.data is the inner member object at runtime.
	const member: MemberDetailData | undefined = query.data;

	return {
		member: member ?? null,
		isLoading: query.isLoading,
		error: query.error,
		refetch: () => query.refetch(),
	};
}
